import type { ReactNode } from 'react';

import { Button } from './Button';
import { Dialog } from './Dialog';

/**
 * "Are you sure?", for the buttons that cannot be undone.
 *
 * A `Dialog` rather than the browser's own confirm box, for the reasons the platform panel
 * gives: the browser's own is a different typeface on a different ground, and on a screen
 * whose whole risk is pressing the wrong row's button it is the one moment the console
 * stops looking like itself. Wrapped here so that a screen asks the question in one line instead of
 * assembling the same two buttons a third time — and so every confirmation puts the safe
 * button first and the dangerous one last, in the console's own words.
 *
 * `open` is whether there is something to confirm; the caller keeps what that is.
 */
export function ConfirmDialog({
  open,
  title,
  confirm,
  cancel,
  onConfirm,
  onClose,
  children,
}: {
  open: boolean;
  title: ReactNode;
  /** The dangerous button's words — the verb from the button that opened this. */
  confirm: ReactNode;
  cancel: ReactNode;
  onConfirm: () => void;
  onClose: () => void;
  /** The question, naming the thing about to go. */
  children?: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      title={title}
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose}>
            {cancel}
          </Button>
          <Button variant="danger" onClick={onConfirm}>
            {confirm}
          </Button>
        </>
      }
    >
      <p className="m-0 leading-[1.45]">{children}</p>
    </Dialog>
  );
}
